import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  MapPin,
  Send,
  Pencil,
  UserPlus,
  CheckCircle2,
  XCircle,
  Package,
  Trash2,
  ArrowDownLeft,
} from 'lucide-react'
import { Button } from '../lib/shadcn/button'
import { Badge } from '../lib/shadcn/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../lib/shadcn/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../lib/shadcn/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { toast } from '../lib/shadcn/sonner'
import { useOnRestored, useUndoToast } from '../hooks/useUndoToast'
import EmailBody from '../components/EmailBody'
import {
  useGetLeadDetail,
  useGetCustomerDetail,
  useUpdateLead,
  useConvertLead,
  useGetCrmSettings,
  useDeleteCommunication,
  useSyncInboundEmails,
} from '../hooks/backend/crm'
import { formatCurrency, formatDate, formatDateTime } from '../utils/format'
import RelationshipCard, { type Stats } from './ui/RelationshipCard'
import LeadFormDialog, { STAGES, type Lead, type Stage } from './ui/LeadFormDialog'
import CustomerEditDialog from './ui/CustomerEditDialog'
import MessageDialog, { type MessageTarget } from './ui/MessageDialog'
import ContactEmails from './ui/ContactEmails'
import CustomerOtherEmails from './ui/CustomerOtherEmails'
import EnrichmentCard, { type Enrichment } from './ui/EnrichmentCard'
import CustomerOverview, {
  type CustomerNotes,
  type OrderPace,
  type OrderWithItems,
  type UsualItem,
} from './ui/CustomerOverview'

interface Communication {
  id: number
  channel: 'email' | 'sms'
  direction?: 'inbound' | 'outbound'
  subject: string | null
  body: string
  status: string
  error: string | null
  sent_by: string | null
  sent_at: string
}

type RecentOrder = OrderWithItems

interface ProductOrdered {
  item_name: string
  item_sku: string
  total_quantity: number
  times_ordered: number
  last_ordered: string | null
}

interface CustomerRecord {
  customer_id: string
  contact_name: string | null
  company_name: string | null
  contact_number: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  billing_phone: string | null
  shipping_phone: string | null
  billing_address: string | null
  billing_city: string | null
  billing_state: string | null
  billing_zip: string | null
  delivery_method: string | null
  website?: string | null
  facebook_url?: string | null
  instagram_url?: string | null
  twitter_url?: string | null
  linkedin_url?: string | null
  tiktok_url?: string | null
  youtube_url?: string | null
  enrichment_summary?: string | null
  enriched_at?: string | null
  enriched_by?: string | null
}

function InfoRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2 items-start text-sm py-1">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-foreground break-words">{children}</span>
    </div>
  )
}

export default function ContactDetailPage({ kind }: { kind: 'lead' | 'customer' }) {
  const params = useParams()
  const navigate = useNavigate()
  const getLeadDetail = useGetLeadDetail()
  const getCustomerDetail = useGetCustomerDetail()
  const updateLead = useUpdateLead()
  const convertLead = useConvertLead()
  const getCrmSettings = useGetCrmSettings()
  const deleteCommunication = useDeleteCommunication()
  const showUndo = useUndoToast()
  const syncInbound = useSyncInboundEmails()

  const [loading, setLoading] = useState(true)
  const [lead, setLead] = useState<Lead | null>(null)
  const [customer, setCustomer] = useState<CustomerRecord | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [orders, setOrders] = useState<RecentOrder[]>([])
  const [products, setProducts] = useState<ProductOrdered[]>([])
  const [comms, setComms] = useState<Communication[]>([])
  const [pace, setPace] = useState<OrderPace | null>(null)
  const [usualItems, setUsualItems] = useState<UsualItem[]>([])
  const [notes, setNotes] = useState<CustomerNotes | null>(null)
  const [tab, setTab] = useState(kind === 'customer' ? 'overview' : 'timeline')

  const [messageTarget, setMessageTarget] = useState<MessageTarget | null>(null)
  const [editLeadOpen, setEditLeadOpen] = useState(false)
  const [editCustomerOpen, setEditCustomerOpen] = useState(false)
  const [converting, setConverting] = useState(false)

  const smsConfigured = Boolean((getCrmSettings.data as { sms_configured?: boolean } | undefined)?.sms_configured)

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    let contactEmail = ''
    try {
      if (kind === 'lead') {
        const id = Number(params['id'])
        const data = (await getLeadDetail.trigger({ id }, { skipCache: true })) as {
          lead: Lead
          stats: Stats | null
          recent_orders: RecentOrder[]
          products: ProductOrdered[]
          communications: Communication[]
        }
        setLead(data.lead)
        setCustomer(null)
        setStats(data.stats)
        setOrders(data.recent_orders ?? [])
        setProducts(data.products ?? [])
        setComms(data.communications ?? [])
        contactEmail = data.lead?.email ?? ''
      } else {
        const id = params['id'] ?? ''
        const data = (await getCustomerDetail.trigger({ customer_id: id }, { skipCache: true })) as {
          customer: CustomerRecord
          stats: Stats | null
          recent_orders: RecentOrder[]
          products: ProductOrdered[]
          communications: Communication[]
          pace?: OrderPace
          usual_items?: UsualItem[]
          notes?: CustomerNotes
        }
        setPace(data.pace ?? null)
        setUsualItems(data.usual_items ?? [])
        setNotes(data.notes ?? null)
        setCustomer(data.customer)
        setLead(null)
        setStats(data.stats)
        setOrders(data.recent_orders ?? [])
        setProducts(data.products ?? [])
        setComms(data.communications ?? [])
        contactEmail = data.customer?.email ?? ''
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load details')
    } finally {
      if (!silent) setLoading(false)
    }

    // Monitor the mailbox for new inbound email from this contact and attach any
    // new matches to the timeline. Runs in the background; only refreshes the
    // timeline when something new was ingested. Never runs on a silent refresh
    // to avoid re-triggering itself.
    if (!silent && contactEmail.trim()) {
      try {
        const result = (await syncInbound.trigger({ email: contactEmail.trim() })) as {
          inserted?: number
        }
        if ((result?.inserted ?? 0) > 0) {
          await load(true)
        }
      } catch {
        // Ignore sync failures (e.g. mailbox not connected) — keep the timeline.
      }
    }
  }

  useEffect(() => {
    setTab(kind === 'customer' ? 'overview' : 'timeline')
  }, [kind, params['id']])

  useEffect(() => {
    getCrmSettings.trigger()
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, params['id']])

  // Must stay above the loading return: hooks have to run on every render.
  useOnRestored(['communication'], () => void load(true))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    )
  }

  const isLead = kind === 'lead'
  const name = isLead ? lead?.name : customer?.company_name || customer?.contact_name || customer?.customer_id
  const email = isLead ? lead?.email ?? '' : customer?.email ?? ''
  const phone = isLead ? lead?.phone ?? '' : customer?.billing_phone || customer?.shipping_phone || ''
  const location =
    !isLead && customer
      ? [customer.billing_city, customer.billing_state].filter(Boolean).join(', ')
      : ''

  const enrichment: Enrichment | null = isLead
    ? lead
      ? {
          website: lead.website ?? null,
          facebook_url: lead.facebook_url ?? null,
          instagram_url: lead.instagram_url ?? null,
          twitter_url: lead.twitter_url ?? null,
          linkedin_url: lead.linkedin_url ?? null,
          tiktok_url: lead.tiktok_url ?? null,
          youtube_url: lead.youtube_url ?? null,
          enrichment_summary: lead.enrichment_summary ?? null,
          enriched_at: lead.enriched_at ?? null,
          enriched_by: lead.enriched_by ?? null,
        }
      : null
    : customer
      ? {
          website: customer.website ?? null,
          facebook_url: customer.facebook_url ?? null,
          instagram_url: customer.instagram_url ?? null,
          twitter_url: customer.twitter_url ?? null,
          linkedin_url: customer.linkedin_url ?? null,
          tiktok_url: customer.tiktok_url ?? null,
          youtube_url: customer.youtube_url ?? null,
          enrichment_summary: customer.enrichment_summary ?? null,
          enriched_at: customer.enriched_at ?? null,
          enriched_by: customer.enriched_by ?? null,
        }
      : null

  const enrichmentId = isLead ? lead?.id : customer?.customer_id

  const openMessage = () => {
    if (isLead && lead) {
      setMessageTarget({ leadId: lead.id, name: lead.name, email: lead.email, phone: lead.phone })
    } else if (customer) {
      setMessageTarget({
        customerId: customer.customer_id,
        name: name ?? '',
        email: customer.email ?? '',
        phone: customer.billing_phone || customer.shipping_phone || '',
      })
    }
  }

  const handleStageChange = async (stage: Stage) => {
    if (!lead) return
    try {
      await updateLead.trigger({ id: lead.id, stage })
      setLead({ ...lead, stage })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update stage')
    }
  }

  const handleConvert = async () => {
    if (!lead) return
    setConverting(true)
    try {
      const result = (await convertLead.trigger({ id: lead.id })) as { already_converted?: boolean; warnings?: string[] }
      toast.success(result.already_converted ? 'Already a customer' : `${lead.name} converted to a customer`)
      for (const w of result.warnings ?? []) toast.warning(w, { duration: 10000 })
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to convert lead')
    } finally {
      setConverting(false)
    }
  }

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts the message back.
  const handleDeleteComm = async (item: Communication) => {
    const id = item.id
    try {
      const result = (await deleteCommunication.trigger({ id })) as { trash_id?: number | null } | undefined
      setComms(prev => prev.filter(c => c.id !== id))
      showUndo('Message deleted', result?.trash_id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete message')
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">{name || '—'}</h1>
          {location && <p className="text-sm text-muted-foreground mt-0.5">{location}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {isLead && lead ? (
            <>
              <Select value={lead.stage} onValueChange={v => handleStageChange(v as Stage)}>
                <SelectTrigger className="h-9 w-[130px]">
                  <Badge variant="secondary">{lead.stage}</Badge>
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!lead.converted_customer_id && (
                <Button variant="outline" onClick={handleConvert} disabled={converting}>
                  {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Convert
                </Button>
              )}
            </>
          ) : (
            <Badge variant="outline" className="capitalize">
              {customer?.delivery_method?.replace('_', ' ') || 'Customer'}
            </Badge>
          )}
          <Button onClick={openMessage}>
            <Send className="w-4 h-4" /> Message
          </Button>
        </div>
      </div>

      <button
        onClick={() => navigate(isLead ? '/crm' : '/customers')}
        className="mt-5 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" /> {isLead ? 'All leads' : 'All customers'}
      </button>

      {/* Body: main + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
        {/* Main */}
        <div className="lg:col-span-2">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {!isLead && <TabsTrigger value="overview">Overview</TabsTrigger>}
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
            </TabsList>

            {/* Overview (customers): pace, usual order, invoices, notes */}
            {!isLead && customer && (
              <TabsContent value="overview" className="pt-3">
                <CustomerOverview
                  customerId={customer.customer_id}
                  name={name ?? ''}
                  firstName={customer.first_name ?? ''}
                  email={email}
                  phone={phone}
                  pace={pace}
                  usualItems={usualItems}
                  orders={orders}
                  notes={notes}
                  onMessage={setMessageTarget}
                  onNotesSaved={text => setNotes(prev => (prev ? { ...prev, notes: text } : prev))}
                  onShowAllOrders={() => setTab('orders')}
                />
              </TabsContent>
            )}

            {/* Timeline */}
            <TabsContent value="timeline" className="pt-3">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">Activity timeline</h2>
              </div>
              {comms.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No activity yet. Emails and text messages will appear here.
                </div>
              ) : (
                <div className="space-y-2">
                  {comms.map(item => (
                    <div key={item.id} className="group rounded-md border border-border p-3 text-sm bg-card">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="gap-1">
                          {item.channel === 'email' ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                          {item.channel.toUpperCase()}
                        </Badge>
                        {item.direction === 'inbound' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400">
                            <ArrowDownLeft className="w-3 h-3" /> Received
                          </span>
                        ) : item.status === 'sent' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-3 h-3" /> Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-destructive">
                            <XCircle className="w-3 h-3" /> Failed
                          </span>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground">{formatDateTime(item.sent_at)}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          aria-label="Delete message"
                          onClick={() => void handleDeleteComm(item)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {item.subject && <p className="font-medium text-foreground">{item.subject}</p>}
                      <EmailBody body={item.body} className="text-muted-foreground" />
                      {item.error && <p className="text-xs text-destructive mt-1">{item.error}</p>}
                      {item.sent_by && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.direction === 'inbound' ? `from ${item.sent_by}` : `by ${item.sent_by}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Emails */}
            <TabsContent value="emails" className="pt-3">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">Email conversation</h2>
              </div>
              <ContactEmails email={email} name={name ?? ''} />
            </TabsContent>

            {/* Orders */}
            <TabsContent value="orders" className="pt-3">
              {orders.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No orders yet.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        {!isLead && <TableHead>Items</TableHead>}
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map(o => (
                        <TableRow key={o.salesorder_number}>
                          <TableCell className="font-medium text-foreground">{o.salesorder_number}</TableCell>
                          <TableCell className="text-muted-foreground">{formatDate(o.order_date)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">{o.status || '—'}</Badge>
                          </TableCell>
                          {!isLead && (
                            <TableCell className="text-xs text-muted-foreground whitespace-normal">
                              {o.items && o.items.length > 0
                                ? o.items.map(i => `${i.quantity} ${i.item_name}`).join(' · ')
                                : '—'}
                            </TableCell>
                          )}
                          <TableCell className="text-right tabular-nums">{formatCurrency(o.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Products */}
            <TabsContent value="products" className="pt-3">
              {products.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No products ordered yet.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">Last</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map(p => (
                        <TableRow key={p.item_sku || p.item_name}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Package className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="font-medium text-foreground">{p.item_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{p.total_quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.times_ordered}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{formatDate(p.last_ordered)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact info */}
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-foreground">Contact info</h3>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                onClick={() => (isLead ? setEditLeadOpen(true) : setEditCustomerOpen(true))}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            </div>
            {!isLead && customer?.contact_number && (
              <InfoRow icon={<span className="w-4" />} label="Cust #">{customer.contact_number}</InfoRow>
            )}
            {!isLead && (customer?.first_name || customer?.last_name) && (
              <InfoRow icon={<span className="w-4" />} label="Contact">
                {`${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`.trim()}
              </InfoRow>
            )}
            <InfoRow icon={<Mail className="w-4 h-4" />} label="Email">
              {email ? <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a> : '—'}
            </InfoRow>
            {!isLead && customer && <CustomerOtherEmails customerId={customer.customer_id} />}
            <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone">{phone || '—'}</InfoRow>
            {isLead && lead?.notes && (
              <div className="mt-2 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground mb-1">Notes</div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* Web presence enrichment */}
          {enrichment && enrichmentId !== undefined && (
            <EnrichmentCard
              kind={kind}
              id={enrichmentId}
              data={enrichment}
              onEnriched={() => void load()}
            />
          )}

          {/* Address (customers only) */}
          {!isLead && customer && (customer.billing_address || customer.billing_city) && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground" /> Address
              </h3>
              <p className="text-sm text-foreground whitespace-pre-line">
                {[
                  customer.billing_address,
                  [customer.billing_city, customer.billing_state].filter(Boolean).join(', '),
                  customer.billing_zip,
                ]
                  .filter(Boolean)
                  .join('\n')}
              </p>
            </div>
          )}

          {/* Relationship */}
          {stats && (!isLead || (lead && lead.converted_customer_id)) ? (
            <RelationshipCard stats={stats} source={!isLead ? customer?.delivery_method ?? undefined : undefined} />
          ) : isLead ? (
            <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              Convert this lead to a customer to track order history.
            </div>
          ) : null}
        </div>
      </div>

      {/* Dialogs */}
      <MessageDialog
        target={messageTarget}
        smsConfigured={smsConfigured}
        onOpenChange={o => {
          if (!o) {
            setMessageTarget(null)
            void load()
          }
        }}
      />
      <LeadFormDialog
        open={editLeadOpen}
        lead={lead}
        onOpenChange={setEditLeadOpen}
        onSaved={() => void load()}
      />
      <CustomerEditDialog
        customerId={editCustomerOpen ? customer?.customer_id ?? null : null}
        onOpenChange={o => setEditCustomerOpen(o)}
        onSaved={() => void load()}
      />

    </div>
  )
}
